import { useState } from'react';
import { motion, AnimatePresence } from'framer-motion';
import { Crown, Flame, Sparkles } from'lucide-react';
import { colorSymbolism, chariotComponents } from'../../data/frameworks';
import { MerkavahChariot } from'../interactive/MerkavahChariot';

export default function Step4() {
 const [selectedColor, setSelectedColor] = useState<string | null>(null);

 return (
 <div className="section-container">
 {/* Introduction */}
 <div className="max-w-4xl mx-auto mb-16">
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 className="text-center mb-12"
 >
 <p className="hebrew-text text-2xl md:text-3xl text-primary-600 dark:text-primary-400 mb-4">
 וממעל לרקיע אשר על ראשם כמראה אבן ספיר דמות כסא
 </p>
 <p className="text-lg text-[var(--color-muted)] italic">
"And above the firmament that was over their heads was the likeness
 of a throne, as the appearance of a sapphire stone..."
 </p>
 <p className="text-sm text-[var(--color-muted)] mt-2">- Ezekiel 1:26</p>
 </motion.div>

 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.2 }}
 className="prose prose-lg dark:prose-invert mx-auto"
 >
 <p>
 The culmination of Ezekiel's vision is the throne - the Kiseh HaKavod (Throne of Glory).
 Above the living creatures and the awesome firmament sits this sapphire throne,
 upon which appears the likeness of a human form wreathed in fire and surrounded
 by rainbow radiance.
 </p>
 </motion.div>
 </div>

 {/* Throne Visualization Card */}
 <motion.div
 initial={{ opacity: 0, scale: 0.9 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ delay: 0.3 }}
 className="mb-16"
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">
 The <span className="text-flame">Throne of Glory</span>
 </h2>

 <div className="vision-container max-w-2xl mx-auto p-8 relative">
 {/* Stylized throne representation */}
 <div className="relative flex flex-col items-center">
 {/* Rainbow aura */}
 <div className="absolute inset-0 flex items-center justify-center">
 <div className="w-80 h-80 rounded-full opacity-30" style={{
 background:'conic-gradient(from 0deg, #ef4444, #f97316, #eab308, #22c55e, #3b82f6, #8b5cf6, #ef4444)'
 }} />
 </div>

 {/* Divine presence glow */}
 <motion.div
 animate={{
 scale: [1, 1.1, 1],
 opacity: [0.6, 0.8, 0.6]
 }}
 transition={{
 duration: 3,
 repeat: Infinity,
 ease:"easeInOut"
 }}
 className="w-32 h-40 rounded-full bg-gradient-to-b from-amber-300 via-amber-500 to-orange-600 flex items-center justify-center mb-4 z-10 shadow-lg shadow-amber-500/50"
 >
 <div className="w-20 h-24 rounded-full bg-gradient-to-b from-white via-amber-200 to-amber-400 flex items-center justify-center">
 <Sparkles className="w-10 h-10 text-amber-700" />
 </div>
 </motion.div>

 {/* Throne shape */}
 <div className="relative z-10">
 {/* Throne back */}
 <div className="w-48 h-32 bg-gradient-to-b from-blue-600 via-blue-700 to-blue-900 rounded-t-3xl flex items-center justify-center shadow-lg">
 <div className="w-40 h-24 bg-gradient-to-b from-blue-500 to-blue-700 rounded-t-2xl flex items-center justify-center">
 <Crown className="w-12 h-12 text-[#3366cc]" />
 </div>
 </div>
 {/* Throne seat */}
 <div className="w-56 h-8 bg-gradient-to-r from-blue-800 via-blue-600 to-blue-800 rounded-b-lg shadow-lg" />
 </div>

 {/* Labels */}
 <div className="mt-8 text-center">
 <p className="hebrew-text text-2xl text-[#3366cc] mb-2">כסא הכבוד</p>
 <p className="text-sm text-white/70">Throne of Glory</p>
 </div>
 </div>
 </div>
 </motion.div>

 {/* Kavod Explanation */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.4 }}
 className="max-w-4xl mx-auto mb-16"
 >
 <div className="card p-8 transition-all duration-300 hover:shadow-lg">
 <h3 className="text-2xl font-semibold mb-4 text-center">
 <span className="hebrew-text text-xl text-primary-600 dark:text-primary-400 mr-2">כבוד</span>
 Kavod - The Divine Glory
 </h3>

 <p className="text-[var(--color-muted)] mb-6 text-center">
 The Hebrew word"Kavod" means both"glory" and"weight/presence." It refers to
 the manifestation of divine presence that can be perceived, however dimly,
 by human awareness. Ezekiel describes seeing"the appearance of the likeness
 of the glory of the Lord" - multiple layers of qualification emphasizing
 that this is not God's essence, but God's self-revelation.
 </p>

 <blockquote className="border-l-4 border-primary-600 pl-6 py-4 bg-primary-600/5 rounded-r-lg">
 <p className="italic text-[var(--color-muted)]">
"And upon the likeness of the throne was the likeness as the appearance
 of a man above upon it. And I saw as the color of amber (hashmal), as the
 appearance of fire round about within it, from the appearance of his loins
 upward, and from the appearance of his loins downward, I saw as it were
 the appearance of fire, and it had brightness round about."
 </p>
 <footer className="mt-2 text-sm text-primary-600 dark:text-primary-400">
 - Ezekiel 1:26-27
 </footer>
 </blockquote>
 </div>
 </motion.div>

 {/* Interactive Color Symbolism */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.5 }}
 className="mb-16"
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-4">
 Colors of the <span className="text-[#202122] font-bold font-serif">Divine Vision</span>
 </h2>
 <p className="text-center text-[var(--color-muted)] mb-8 max-w-xl mx-auto">
 Click each color to explore its mystical significance
 </p>

 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
 {Object.entries(colorSymbolism).map(([key, color], index) => {
 const colorStyle = key ==='sapphire' ?'linear-gradient(135deg, #1e3a8a, #3b82f6)'
 : key ==='amber' ?'linear-gradient(135deg, #d97706, #fcd34d)'
 : key ==='fire' ?'linear-gradient(135deg, #dc2626, #f97316)'
 : key ==='crystal' ?'linear-gradient(135deg, #e5e7eb, #ffffff)'
 :'linear-gradient(135deg, #ef4444, #f97316, #eab308, #22c55e, #3b82f6, #8b5cf6)';

 return (
 <motion.button
 key={key}
 initial={{ opacity: 0, scale: 0.95 }}
 animate={{ opacity: 1, scale: 1 }}
 transition={{ delay: 0.6 + index * 0.1 }}
 onClick={() => setSelectedColor(selectedColor === key ? null : key)}
 className={`card group text-left transition-all duration-300 hover:shadow-lg ${
 selectedColor === key ?'ring-2 ring-primary-500' :''
 }`}
 >
 <div className="flex items-center gap-3 mb-4">
 <div
 className="w-14 h-14 rounded-full shadow-lg transition-all duration-300 group-hover:scale-110"
 style={{ background: colorStyle }}
 />
 <div>
 <h3 className="text-lg font-semibold capitalize">{key}</h3>
 <p className="hebrew-text text-primary-600 dark:text-primary-400">
 {color.hebrew}
 </p>
 </div>
 </div>
 <p className="text-[var(--color-muted)] text-sm mb-2">{color.meaning}</p>

 <AnimatePresence>
 {selectedColor === key && (
 <motion.div
 initial={{ opacity: 0, height: 0 }}
 animate={{ opacity: 1, height:'auto' }}
 exit={{ opacity: 0, height: 0 }}
 className="overflow-hidden"
 >
 <div className="pt-3 mt-3 border-t border-[var(--color-border)]">
 <p className="text-xs text-secondary-600 dark:text-secondary-400 italic">
 {color.usage}
 </p>
 </div>
 </motion.div>
 )}
 </AnimatePresence>
 </motion.button>
 );
 })}
 </div>
 </motion.div>

 {/* Interactive Merkavah Chariot Visualization */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.65 }}
 className="mb-16"
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">
 Interactive <span className="text-flame">Merkavah Chariot</span>
 </h2>
 <MerkavahChariot className="max-w-4xl mx-auto" />
 </motion.div>

 {/* Chariot Components Summary */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.7 }}
 className="mb-16"
 >
 <h2 className="text-2xl md:text-3xl font-display font-bold text-center mb-8">
 Complete <span className="text-flame">Chariot Structure</span>
 </h2>

 <div className="max-w-3xl mx-auto">
 <div className="relative">
 {/* Vertical line */}
 <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-secondary-600 via-primary-600 to-secondary-600" />

 {/* Components */}
 {Object.entries(chariotComponents).map(([key, component], index) => (
 <motion.div
 key={key}
 initial={{ opacity: 0, x: -20 }}
 animate={{ opacity: 1, x: 0 }}
 transition={{ delay: 0.8 + index * 0.1 }}
 className="relative pl-20 pb-8"
 >
 {/* Marker */}
 <div className="absolute left-4 w-8 h-8 rounded-full bg-gradient-to-r from-primary-600 to-secondary-700 flex items-center justify-center text-white text-sm font-bold transition-all duration-300 hover:scale-110">
 {index + 1}
 </div>

 <div className="card transition-all duration-300 hover:shadow-lg hover:border-primary-500/50">
 <h3 className="text-lg font-semibold">{component.name}</h3>
 <p className="hebrew-text text-primary-600 dark:text-primary-400 mb-2">
 {component.hebrewName}
 </p>
 <p className="text-[var(--color-muted)] text-sm mb-2">
 {component.description}
 </p>
 <p className="text-xs text-secondary-600 dark:text-secondary-400 italic">
 {component.significance}
 </p>
 </div>
 </motion.div>
 ))}
 </div>
 </div>
 </motion.div>

 {/* The Response */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 0.9 }}
 className="p-8 rounded-2xl bg-gradient-to-r from-primary-600/10 to-secondary-700/10 border border-primary-600/20"
 >
 <h3 className="text-xl font-semibold mb-6 text-center">Ezekiel's Response</h3>

 <div className="max-w-2xl mx-auto text-center">
 <p className="hebrew-text text-xl text-primary-600 dark:text-primary-400 mb-4">
 ואראה כמראה אש וכנגה לו סביב כמראה הקשת אשר יהיה בענן ביום הגשם...
 ואפל על פני
 </p>
 <p className="text-[var(--color-muted)] italic mb-4">
"Like the appearance of the rainbow in the clouds on a rainy day,
 so was the appearance of the brightness all around. This was the appearance
 of the likeness of the glory of the Lord. And when I saw it,
 I fell upon my face..."
 </p>
 <p className="text-sm text-[var(--color-muted)]">- Ezekiel 1:28</p>

 <div className="mt-8 p-4 rounded-lg bg-[var(--color-background)]">
 <p className="text-[var(--color-muted)]">
 The appropriate human response to divine revelation is not comprehension
 but prostration - falling upon one's face in awe and humility.
 The vision is not given to satisfy curiosity but to transform the seer.
 </p>
 </div>
 </div>
 </motion.div>

 {/* Mathematical Structure */}
 <motion.div
 initial={{ opacity: 0, y: 20 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ delay: 1 }}
 className="mt-12 p-8 rounded-2xl bg-[var(--color-card)] border border-[var(--color-border)]"
 >
 <h3 className="text-xl font-semibold mb-6 text-center">
 Sacred Structure of the Vision
 </h3>

 <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 text-center">
 <div className="p-4 rounded-lg bg-primary-600/10 transition-all duration-300 hover:scale-105">
 <div className="text-3xl font-bold text-flame mb-1">1</div>
 <p className="text-sm text-[var(--color-muted)]">Throne</p>
 </div>
 <div className="p-4 rounded-lg bg-secondary-700/10 transition-all duration-300 hover:scale-105">
 <div className="text-3xl font-bold text-[#202122] font-bold font-serif mb-1">1</div>
 <p className="text-sm text-[var(--color-muted)]">Firmament</p>
 </div>
 <div className="p-4 rounded-lg bg-primary-600/10 transition-all duration-300 hover:scale-105">
 <div className="text-3xl font-bold text-flame mb-1">4</div>
 <p className="text-sm text-[var(--color-muted)]">Creatures</p>
 </div>
 <div className="p-4 rounded-lg bg-secondary-700/10 transition-all duration-300 hover:scale-105">
 <div className="text-3xl font-bold text-[#202122] font-bold font-serif mb-1">4</div>
 <p className="text-sm text-[var(--color-muted)]">Wheels</p>
 </div>
 <div className="p-4 rounded-lg bg-primary-600/10 transition-all duration-300 hover:scale-105">
 <div className="text-3xl font-bold text-flame mb-1">7</div>
 <p className="text-sm text-[var(--color-muted)]">Colors</p>
 </div>
 </div>
 </motion.div>
 </div>
 );
}
